import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  createDepartmentSchema,
  updateDepartmentSchema,
  type CreateDepartmentFormData,
  type UpdateDepartmentFormData,
} from '@/lib/validations/auth'
import { sanitizeRestrictedLabelInput } from '@/lib/validations/restrictedLabel'

interface DepartmentFormProps {
  departmentId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type DepartmentFormValues = CreateDepartmentFormData | UpdateDepartmentFormData

export function DepartmentForm({ departmentId, onSuccess, onCancel }: DepartmentFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!departmentId

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DepartmentFormValues>({
    resolver: zodResolver(isEditMode ? updateDepartmentSchema : createDepartmentSchema),
    defaultValues: {
      name: '',
      code: '',
      descriptions: '',
    },
  })

  useEffect(() => {
    if (isEditMode && departmentId) {
      setIsLoading(true)

      apiClient
        .get(`/departments/${departmentId}`)
        .then(({ data }) => {
          const department = data?.data || data || {}

          reset({
            name: sanitizeRestrictedLabelInput(department.name || ''),
            code: sanitizeRestrictedLabelInput(department.code || ''),
            descriptions: department.descriptions
              ? sanitizeRestrictedLabelInput(department.descriptions)
              : '',
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [departmentId, isEditMode, reset, showError])

  const onSubmit = async (data: DepartmentFormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: sanitizeRestrictedLabelInput(data.name),
        code: sanitizeRestrictedLabelInput(data.code),
        descriptions: data.descriptions
          ? sanitizeRestrictedLabelInput(data.descriptions) || null
          : null,
      }

      if (isEditMode) {
        await apiClient.put(`/departments/${departmentId}`, payload)
        showSuccess('Success', 'Division updated successfully')
      } else {
        await apiClient.post('/departments', payload)
        showSuccess('Success', 'Division created successfully')
      }

      onSuccess?.() ?? navigate('/departments')
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/departments')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading division...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">

        {/* Name */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>

          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Human Resources" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The division name.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Code */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>

          <FormControl isInvalid={!!errors.code}>
            <Input {...register('code')} placeholder="e.g. HR, IT, FIN" />
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            <FormHelperText>Short identifier for the division.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Description */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>

          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Enter division description..."
              rows={4}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
            <FormHelperText>Optional description for this division.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Actions */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Division'}
          </Button>
        </HStack>

      </VStack>
    </Box>
  )
}